import { useState, useEffect, useRef } from 'react';
import { Plus, Pencil, Trash2, GripVertical, Camera, Bell, Wifi, Tv, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getServices, createService, updateService, deleteService, uploadFile } from '@/lib/api';

const iconOptions = [
  { value: 'Camera', label: 'Kamera', icon: Camera },
  { value: 'Bell', label: 'Dzwonek', icon: Bell },
  { value: 'Wifi', label: 'WiFi', icon: Wifi },
  { value: 'Tv', label: 'TV', icon: Tv },
];

const iconMap = {
  Camera,
  Bell,
  Wifi,
  Tv,
};

export const ServicesManager = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: 'Camera',
    order: 0,
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const fetchServices = async () => {
    try {
      const response = await getServices();
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleOpenDialog = (service = null) => {
    if (service) {
      setSelectedService(service);
      setFormData({
        title: service.title,
        description: service.description,
        icon: service.icon,
        order: service.order,
      });
      setImagePreview(service.image_url || null);
    } else {
      setSelectedService(null);
      setFormData({
        title: '',
        description: '',
        icon: 'Camera',
        order: 0,
      });
      setImagePreview(null);
    }
    setSelectedImage(null);
    setDialogOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Wybierz plik obrazu');
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Wypełnij wszystkie wymagane pola');
      return;
    }

    setSubmitting(true);
    try {
      let serviceData = { ...formData };

      // Upload image if selected
      if (selectedImage) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', selectedImage);
        const uploadRes = await uploadFile(uploadFormData);
        serviceData.image_url = uploadRes.data.url;
      } else if (selectedService) {
        serviceData.image_url = selectedService.image_url || '';
      }

      if (selectedService) {
        await updateService(selectedService.id, serviceData);
        toast.success('Usługa zaktualizowana');
      } else {
        await createService(serviceData);
        toast.success('Usługa dodana');
      }
      setDialogOpen(false);
      fetchServices();
    } catch (error) {
      toast.error('Wystąpił błąd');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedService) return;
    
    try {
      await deleteService(selectedService.id);
      toast.success('Usługa usunięta');
      setDeleteDialogOpen(false);
      setSelectedService(null);
      fetchServices();
    } catch (error) {
      toast.error('Wystąpił błąd');
    }
  };

  const openDeleteDialog = (service) => {
    setSelectedService(service);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="services-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Usługi</h1>
          <p className="text-slate-400">Zarządzaj usługami wyświetlanymi na stronie</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="h-11 px-5 rounded-full bg-gradient-to-r from-cyan-400 to-cyan-600 text-[#0A0F1C] hover:from-cyan-300 hover:to-cyan-500 font-semibold shadow-lg shadow-cyan-500/30"
          data-testid="add-service-button"
        >
          <Plus className="w-5 h-5 mr-2" />
          Dodaj usługę
        </Button>
      </div>

      <div className="bg-[#111827] rounded-xl border border-cyan-500/10 overflow-hidden">
        <div className="divide-y divide-cyan-500/10">
          {services.length === 0 ? (
            <div className="px-6 py-12 text-center text-slate-400">
              Brak usług. Dodaj pierwszą usługę.
            </div>
          ) : (
            services.map((service) => {
              const Icon = iconMap[service.icon] || Camera;
              return (
                <div
                  key={service.id}
                  className="px-6 py-4 flex items-center gap-4 hover:bg-cyan-500/5 transition-colors"
                  data-testid={`service-row-${service.id}`}
                >
                  <GripVertical className="w-5 h-5 text-slate-500 cursor-grab" />
                  {service.image_url ? (
                    <div className="w-12 h-12 rounded-xl overflow-hidden border border-cyan-500/20">
                      <img 
                        src={getImageUrl(service.image_url)} 
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 bg-cyan-500/10 border border-cyan-500/20 rounded-xl flex items-center justify-center">
                      <Icon className="w-6 h-6 text-cyan-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white">{service.title}</p>
                    <p className="text-sm text-slate-400 truncate">{service.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenDialog(service)}
                      className="text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10"
                      data-testid={`edit-service-${service.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDeleteDialog(service)}
                      className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                      data-testid={`delete-service-${service.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md bg-[#111827] border-cyan-500/20" data-testid="service-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">
              {selectedService ? 'Edytuj usługę' : 'Dodaj usługę'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedService ? 'Edytuj szczegóły usługi' : 'Wprowadź dane nowej usługi'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Zdjęcie usługi (opcjonalne)
              </label>
              <div 
                className="border-2 border-dashed border-cyan-500/20 rounded-lg p-4 text-center cursor-pointer hover:border-cyan-500/40 transition-colors bg-[#0A0F1C]"
                onClick={() => fileInputRef.current?.click()}
              >
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview.startsWith('data:') ? imagePreview : getImageUrl(imagePreview)}
                      alt="Preview"
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImage(null);
                        setImagePreview(null);
                      }}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <p className="text-sm text-slate-400 mt-2">
                      Kliknij aby zmienić zdjęcie
                    </p>
                  </div>
                ) : (
                  <div className="py-4">
                    <Upload className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                    <p className="text-slate-300">Kliknij aby wybrać zdjęcie</p>
                    <p className="text-xs text-slate-500 mt-1">JPG, PNG, GIF, WEBP</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                data-testid="service-image-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Tytuł *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Np. Monitoring CCTV"
                className="bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500"
                data-testid="service-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Opis *</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Opis usługi..."
                rows={3}
                className="bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500"
                data-testid="service-description-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Ikona (gdy brak zdjęcia)</label>
              <Select
                value={formData.icon}
                onValueChange={(value) => setFormData({ ...formData, icon: value })}
              >
                <SelectTrigger className="bg-[#0A0F1C] border-cyan-500/20 text-white" data-testid="service-icon-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111827] border-cyan-500/20">
                  {iconOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="text-white hover:bg-cyan-500/10">
                      <div className="flex items-center gap-2">
                        <option.icon className="w-4 h-4" />
                        {option.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Kolejność</label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                min={0}
                className="bg-[#0A0F1C] border-cyan-500/20 text-white"
                data-testid="service-order-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-cyan-500/20 text-slate-300 hover:bg-cyan-500/10">
              Anuluj
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-gradient-to-r from-cyan-400 to-cyan-600 text-[#0A0F1C] hover:from-cyan-300 hover:to-cyan-500 font-semibold"
              data-testid="service-submit-button"
            >
              {submitting ? 'Zapisywanie...' : selectedService ? 'Zapisz' : 'Dodaj'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md bg-[#111827] border-cyan-500/20" data-testid="delete-service-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Usuń usługę</DialogTitle>
            <DialogDescription className="text-slate-400">
              Ta operacja jest nieodwracalna.
            </DialogDescription>
          </DialogHeader>
          <p className="text-slate-400 py-4">
            Czy na pewno chcesz usunąć usługę "{selectedService?.title}"? 
            Ta operacja jest nieodwracalna.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-cyan-500/20 text-slate-300 hover:bg-cyan-500/10">
              Anuluj
            </Button>
            <Button 
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-service-button"
            >
              Usuń
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicesManager;
